import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

export default function BlogDetail() {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-testid="blog-detail-loading">
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-testid="blog-detail-not-found">
          <p className="text-muted-foreground mb-4">Article not found</p>
          <Link to="/blog">
            <Button variant="outline">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 lg:py-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Link to="/blog">
          <Button variant="ghost" className="mb-8" data-testid="back-to-blog-button">
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="overflow-hidden">
            <img
              src={blog.image_url}
              alt={i18n.language === 'en' ? blog.title_en : blog.title_as}
              className="w-full h-96 object-cover"
              data-testid="blog-detail-image"
            />
            <div className="p-8 lg:p-12">
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User size={18} />
                  <span>{blog.author}</span>
                </div>
                <span className="px-3 py-1 bg-secondary rounded-full text-xs">{blog.category}</span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-primary mb-6" data-testid="blog-detail-title">
                {i18n.language === 'en' ? blog.title_en : blog.title_as}
              </h1>

              <div className="prose prose-lg max-w-none" data-testid="blog-detail-content">
                <p className="text-foreground leading-relaxed whitespace-pre-line">
                  {i18n.language === 'en' ? blog.content_en : blog.content_as}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
